/**
 * Configurable keyboard shortcut system.
 *
 * Features:
 * - Named actions with configurable key bindings
 * - Modifier support (ctrl, alt, shift, meta)
 * - Prevents conflicts with browser defaults
 * - Skips shortcuts when typing in input fields
 * - Shortcut hint display for tooltips
 *
 * Integrates with:
 * - Panel toggling (settings, layers, insights, etc.)
 * - Search modal (Ctrl+K)
 * - Export (Ctrl+E)
 * - Map controls (zoom, reset view)
 * - Variant switching (Ctrl+1..4)
 */

import { panelManager } from '@/panels/panel-manager';
import { eventBus } from '@/core/events';
import { getMap } from '@/map/engine';
import { switchVariant } from '@/app/layout';
import { exportData } from '@/utils/export';

/** A keyboard shortcut binding. */
export interface ShortcutBinding {
  /** Unique action name. */
  action: string;
  /** Human-readable description. */
  label: string;
  /** Key code (KeyboardEvent.key value, case-insensitive). */
  key: string;
  /** Required modifiers. */
  ctrl?: boolean;
  alt?: boolean;
  shift?: boolean;
  meta?: boolean;
  /** Handler function. */
  handler: () => void;
}

/** Default shortcut bindings. */
function getDefaultBindings(): ShortcutBinding[] {
  return [
    // Search
    {
      action: 'search',
      label: 'Open search',
      key: 'k',
      ctrl: true,
      handler: () => eventBus.emit('panel:toggled', { panelId: 'search', visible: true }),
    },

    // Panels
    {
      action: 'toggle-settings',
      label: 'Toggle settings',
      key: ',',
      ctrl: true,
      handler: () => panelManager.togglePanel('settings'),
    },
    {
      action: 'toggle-insights',
      label: 'Toggle AI insights',
      key: 'i',
      ctrl: true,
      handler: () => panelManager.togglePanel('insights'),
    },
    {
      action: 'toggle-layers',
      label: 'Toggle layer control',
      key: 'l',
      alt: true,
      handler: () => {
        const floatingLayers = document.getElementById('floating-layers');
        if (floatingLayers) {
          floatingLayers.classList.toggle('collapsed');
          const toggle = floatingLayers.querySelector('.floating-layers-toggle');
          if (toggle) {
            toggle.textContent = floatingLayers.classList.contains('collapsed') ? '\u25B8' : '\u25BE';
          }
        }
      },
    },
    {
      action: 'toggle-service-status',
      label: 'Toggle service status',
      key: 's',
      ctrl: true,
      shift: true,
      handler: () => panelManager.togglePanel('service-status'),
    },

    // Export
    {
      action: 'export-events',
      label: 'Export events (JSON)',
      key: 'e',
      ctrl: true,
      handler: () => exportData('events', 'json'),
    },

    // Map controls
    {
      action: 'zoom-in',
      label: 'Zoom in',
      key: '=',
      alt: true,
      handler: () => {
        const map = getMap();
        if (map) map.zoomIn({ duration: 200 });
      },
    },
    {
      action: 'zoom-out',
      label: 'Zoom out',
      key: '-',
      alt: true,
      handler: () => {
        const map = getMap();
        if (map) map.zoomOut({ duration: 200 });
      },
    },
    {
      action: 'reset-view',
      label: 'Reset map view',
      key: '0',
      alt: true,
      handler: () => {
        const map = getMap();
        if (map) map.flyTo({ center: [20, 20], zoom: 2, duration: 800 });
      },
    },

    // Desk presets
    {
      action: 'desk-general',
      label: 'General OSINT desk',
      key: '1',
      ctrl: true,
      handler: () => switchVariant('general'),
    },
    {
      action: 'desk-maritime',
      label: 'Maritime desk',
      key: '2',
      ctrl: true,
      handler: () => switchVariant('maritime'),
    },
    {
      action: 'desk-cyber',
      label: 'Cyber desk',
      key: '3',
      ctrl: true,
      handler: () => switchVariant('cyber'),
    },
    {
      action: 'desk-conflict',
      label: 'Conflict desk',
      key: '4',
      ctrl: true,
      handler: () => switchVariant('conflict'),
    },
    {
      action: 'desk-economic',
      label: 'Economic desk',
      key: '5',
      ctrl: true,
      handler: () => switchVariant('economic'),
    },
    // Close overlay
    {
      action: 'close-panel',
      label: 'Close active panel',
      key: 'Escape',
      handler: () => panelManager.hideActivePanel('overlay'),
    },
  ];
}

/** Active bindings (can be customized). */
let activeBindings: ShortcutBinding[] = [];

/** Check if a keyboard event matches a shortcut binding. */
function matchesBinding(e: KeyboardEvent, binding: ShortcutBinding): boolean {
  if (e.key.toLowerCase() !== binding.key.toLowerCase()) return false;
  if ((binding.ctrl ?? false) !== (e.ctrlKey || e.metaKey)) return false;
  if ((binding.alt ?? false) !== e.altKey) return false;
  if ((binding.shift ?? false) !== e.shiftKey) return false;
  return true;
}

/** Check if the keyboard event target is an input element. */
function isInputFocused(e: KeyboardEvent): boolean {
  const tag = (e.target as HTMLElement)?.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if ((e.target as HTMLElement)?.contentEditable === 'true') return true;
  return false;
}

/** The keydown handler installed on document. */
function handleKeydown(e: KeyboardEvent): void {
  // Allow Escape even in input fields
  if (isInputFocused(e) && e.key !== 'Escape') return;

  for (const binding of activeBindings) {
    if (matchesBinding(e, binding)) {
      e.preventDefault();
      binding.handler();
      return;
    }
  }
}

/**
 * Initialize the keyboard shortcut system with default bindings.
 * Returns a cleanup function that removes the event listener.
 */
export function initKeyboardShortcuts(): () => void {
  activeBindings = getDefaultBindings();
  document.addEventListener('keydown', handleKeydown);

  return () => {
    document.removeEventListener('keydown', handleKeydown);
    activeBindings = [];
  };
}

/** Get all active shortcut bindings (for display in settings). */
export function getActiveBindings(): ReadonlyArray<ShortcutBinding> {
  return activeBindings;
}

/**
 * Format a shortcut binding as a human-readable string.
 * e.g. "Ctrl+K", "Ctrl+Shift+S", "Escape"
 */
export function formatShortcut(binding: ShortcutBinding): string {
  const parts: string[] = [];
  const isMac = typeof navigator !== 'undefined' && navigator.platform.includes('Mac');

  if (binding.ctrl) parts.push(isMac ? '\u2318' : 'Ctrl');
  if (binding.alt) parts.push(isMac ? '\u2325' : 'Alt');
  if (binding.shift) parts.push(isMac ? '\u21E7' : 'Shift');

  // Format key name
  let keyLabel = binding.key;
  if (keyLabel === 'Escape') keyLabel = 'Esc';
  else if (keyLabel === ',') keyLabel = ',';
  else if (keyLabel === '=') keyLabel = '+';
  else if (keyLabel === '-') keyLabel = '-';
  else if (keyLabel.length === 1) keyLabel = keyLabel.toUpperCase();

  parts.push(keyLabel);
  return parts.join(isMac ? '' : '+');
}
